
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { TableCell, TableRow } from '@/components/ui/table';
import { StatusBadge } from './StatusBadge';

interface FlattenedSitemapItem {
  title: string;
  href: string;
  status: 'live' | 'dev' | 'coming-soon';
  description: string;
  icon: React.ComponentType<any>;
  category: string;
  categoryEmoji: string;
}

interface SitemapTableRowProps {
  item: FlattenedSitemapItem;
  index: number;
}

export const SitemapTableRow = ({ item, index }: SitemapTableRowProps) => {
  const IconComponent = item.icon;
  const isLive = item.status === 'live';

  const rowContent = (
    <motion.tr
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.02 }}
      className={`border-b border-juv-rose-clay/10 hover:bg-juv-soft-blush/20 transition-colors ${
        isLive ? 'cursor-pointer' : ''
      }`}
    >
      <TableCell className="py-4">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0 w-8 h-8 bg-juv-soft-blush/30 rounded-lg flex items-center justify-center">
            <IconComponent className="h-4 w-4 text-juv-terracotta" />
          </div>
          <div>
            <div className={`font-medium ${
              isLive 
                ? 'text-juv-deep-chestnut hover:text-juv-terracotta' 
                : 'text-gray-500'
            }`}>
              {item.title}
            </div>
          </div>
        </div>
      </TableCell>
      
      <TableCell className="py-4">
        <div className="flex items-center space-x-2">
          <span className="text-lg">{item.categoryEmoji}</span>
          <span className="text-sm text-juv-deep-chestnut/80">{item.category}</span>
        </div>
      </TableCell>
      
      <TableCell className="py-4">
        <StatusBadge status={item.status} />
      </TableCell>
      
      <TableCell className="py-4">
        <div className="flex items-center space-x-2">
          <code className={`text-sm px-2 py-1 rounded ${
            isLive 
              ? 'bg-green-50 text-green-700 border border-green-200' 
              : 'bg-gray-50 text-gray-500 border border-gray-200'
          }`}>
            {item.href}
          </code>
          {isLive && (
            <ExternalLink className="h-3 w-3 text-juv-terracotta/60" />
          )}
        </div>
      </TableCell>
      
      <TableCell className="py-4">
        <p className={`text-sm leading-relaxed ${
          isLive ? 'text-gray-600' : 'text-gray-500'
        }`}>
          {item.description}
        </p>
      </TableCell>
    </motion.tr>
  );

  if (isLive) {
    return (
      <Link to={item.href} className="contents">
        {rowContent}
      </Link>
    );
  }

  return rowContent;
};
